'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  useContextTabs,
  type ContextTabLocation,
  type ContextTabsSnapshot,
} from '@web/hooks';
import { ConfirmActionModal } from '@web/ui';

import {
  isContextTabWorkspaceUrl,
  resolveContextTabLocation,
} from '../utils/contextTabRoutes';
import {
  loadContextTabsSnapshot,
  saveContextTabsSnapshot,
} from '../utils/contextTabsStorage';

type ContextTabsContextValue = ReturnType<typeof useContextTabs> & {
  currentRouteTabId: string;
  currentRoutePageKey: string;
  selectContextTab: (tabId: string) => void;
  createContextTab: () => void;
  closeContextTab: (tabId: string, discardDirty?: boolean) => void;
  clearContextTabDirty: (tabId: string, after?: () => void) => void;
  navigateInActiveTab: (destination: string) => void;
  navigateBack: (fallback?: string) => void;
};

const ContextTabsContext = createContext<ContextTabsContextValue | null>(null);
const DIRTY_HISTORY_GUARD_KEY = '__netnetContextTabDirtyGuard';

type ContextTabsProviderProps = PropsWithChildren<{
  userId: string;
  workspaceId: string;
  workspaceSlug: string;
  newTabLocation?: ContextTabLocation;
}>;

function currentUrl(pathname: string, search: string) {
  return search ? `${pathname}?${search}` : pathname;
}

function hasDynamicRouteMetadata(pageKey: string) {
  return (
    /^contacts:(company|person):(?!new$)/.test(pageKey) ||
    pageKey.startsWith('jobs:job:') ||
    pageKey.startsWith('chat:conversation:')
  );
}

/**
 * Resolves the tab that owns the URL currently rendered by Next.js.
 * During a tab transition this can intentionally differ from activeTabId.
 */
export function findContextTabOwnerId(
  snapshot: ContextTabsSnapshot,
  renderedUrl: string,
) {
  const activeTab = snapshot.tabs.find(
    (tab) => tab.id === snapshot.activeTabId,
  );
  if (activeTab?.url === renderedUrl) return activeTab.id;

  return (
    snapshot.tabs
      .filter((tab) => tab.url === renderedUrl)
      .sort((a, b) => b.lastActivatedAt - a.lastActivatedAt)[0]?.id ??
    snapshot.activeTabId
  );
}

export function ContextTabsProvider({
  userId,
  workspaceId,
  workspaceSlug,
  newTabLocation,
  children,
}: ContextTabsProviderProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const browserUrl = currentUrl(pathname, searchParams.toString());
  const initialLocation =
    resolveContextTabLocation(browserUrl, workspaceSlug) ??
    resolveContextTabLocation(`/${workspaceSlug}`, workspaceSlug)!;
  const defaultNewTabLocation =
    newTabLocation ??
    resolveContextTabLocation(`/${workspaceSlug}`, workspaceSlug)!;
  const [initialSnapshot] = useState(() =>
    loadContextTabsSnapshot(userId, workspaceId, workspaceSlug),
  );
  const pendingUrlRef = useRef<string | null>(null);
  const initialRouteReconciledRef = useRef(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const dirtyHistoryGuardRef = useRef<{
    tabId: string;
    url: string;
  } | null>(null);
  const ignoreNextPopRef = useRef(false);
  const browserBackPendingRef = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const tabs = useContextTabs({
    initialLocation,
    initialSnapshot,
  });
  const currentRouteTabId = findContextTabOwnerId(tabs.snapshot, browserUrl);
  const currentRoutePageKey =
    resolveContextTabLocation(browserUrl, workspaceSlug)?.pageKey ??
    tabs.activeTab.pageKey;

  const armDirtyHistoryGuard = useCallback((tabId: string, url: string) => {
    if (dirtyHistoryGuardRef.current) return;
    window.history.pushState(
      {
        ...window.history.state,
        [DIRTY_HISTORY_GUARD_KEY]: tabId,
      },
      '',
      url,
    );
    dirtyHistoryGuardRef.current = { tabId, url };
  }, []);

  const releaseDirtyHistoryGuard = useCallback((after?: () => void) => {
    if (!dirtyHistoryGuardRef.current) {
      after?.();
      return;
    }

    dirtyHistoryGuardRef.current = null;
    ignoreNextPopRef.current = true;
    if (after) {
      window.addEventListener('popstate', after, { once: true });
    }
    window.history.back();
  }, []);

  useEffect(() => {
    saveContextTabsSnapshot(userId, workspaceId, tabs.snapshot);
  }, [tabs.snapshot, userId, workspaceId]);

  useEffect(() => {
    const shouldGuard =
      tabs.activeTab.isDirty && currentRouteTabId === tabs.activeTab.id;
    if (shouldGuard) {
      armDirtyHistoryGuard(tabs.activeTab.id, tabs.activeTab.url);
    } else if (dirtyHistoryGuardRef.current) {
      releaseDirtyHistoryGuard();
    }
  }, [
    armDirtyHistoryGuard,
    currentRouteTabId,
    releaseDirtyHistoryGuard,
    tabs.activeTab.id,
    tabs.activeTab.isDirty,
    tabs.activeTab.url,
  ]);

  useEffect(() => {
    const handlePopState = () => {
      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }
      const guard = dirtyHistoryGuardRef.current;
      if (!guard || !tabs.activeTab.isDirty) return;

      dirtyHistoryGuardRef.current = null;
      browserBackPendingRef.current = true;
      pendingActionRef.current = () => {
        browserBackPendingRef.current = false;
        tabs.setTabDirty(guard.tabId, false);
        window.history.back();
      };
      setConfirmOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [tabs]);

  useEffect(() => {
    if (!initialRouteReconciledRef.current) {
      initialRouteReconciledRef.current = true;
      const workspaceRoot = `/${workspaceSlug}`;
      if (
        pathname === workspaceRoot &&
        tabs.activeTab.url !== browserUrl &&
        isContextTabWorkspaceUrl(tabs.activeTab.url, workspaceSlug)
      ) {
        pendingUrlRef.current = tabs.activeTab.url;
        router.replace(tabs.activeTab.url);
        return;
      }
    }

    if (pendingUrlRef.current) {
      if (pendingUrlRef.current === browserUrl) {
        pendingUrlRef.current = null;
      }
      return;
    }

    const location = resolveContextTabLocation(browserUrl, workspaceSlug);
    if (!location) return;
    if (location.url === tabs.activeTab.url) {
      if (
        tabs.activeTab.pageKey !== location.pageKey ||
        (!hasDynamicRouteMetadata(location.pageKey) &&
          (tabs.activeTab.name !== location.name ||
            tabs.activeTab.section !== location.section ||
            tabs.activeTab.contextLabel !== location.contextLabel))
      ) {
        tabs.navigate(
          { ...location, contextLabel: undefined },
          { mode: 'replace', preserveIdentity: false },
        );
      }
      return;
    }
    const reconcileLocation = () => {
      const previousLocation =
        tabs.activeTab.history[tabs.activeTab.historyIndex - 1];
      if (previousLocation?.url === browserUrl) {
        tabs.goBack();
        return;
      }
      const nextLocation =
        tabs.activeTab.history[tabs.activeTab.historyIndex + 1];
      if (nextLocation?.url === browserUrl) {
        tabs.goForward();
        return;
      }
      const previousPath = new URL(tabs.activeTab.url, 'https://netnet.local')
        .pathname;
      tabs.navigate(location, {
        mode: previousPath === pathname ? 'replace' : 'push',
      });
    };

    if (tabs.activeTab.isDirty) {
      const currentTabId = tabs.activeTab.id;
      const currentTabUrl = tabs.activeTab.url;
      pendingUrlRef.current = currentTabUrl;
      router.replace(currentTabUrl);
      pendingActionRef.current = () => {
        tabs.setTabDirty(currentTabId, false);
        reconcileLocation();
        pendingUrlRef.current = location.url;
        router.push(location.url);
      };
      queueMicrotask(() => setConfirmOpen(true));
      return;
    }

    reconcileLocation();
  }, [browserUrl, pathname, router, tabs, workspaceSlug]);

  const navigateToTab = useCallback(
    (location: ContextTabLocation) => {
      if (location.url === browserUrl) return;
      pendingUrlRef.current = location.url;
      router.push(location.url);
    },
    [browserUrl, router],
  );

  const runGuarded = useCallback(
    (tabId: string, action: () => void) => {
      const tab = tabs.snapshot.tabs.find((item) => item.id === tabId);
      if (!tab?.isDirty) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setConfirmOpen(true);
    },
    [setConfirmOpen, tabs.snapshot.tabs],
  );

  const clearContextTabDirty = useCallback(
    (tabId: string, after?: () => void) => {
      releaseDirtyHistoryGuard(() => {
        tabs.setTabDirty(tabId, false);
        after?.();
      });
    },
    [releaseDirtyHistoryGuard, tabs],
  );

  const selectContextTab = useCallback(
    (tabId: string) => {
      const tab = tabs.snapshot.tabs.find((item) => item.id === tabId);
      if (!tab) return;
      if (tabId === tabs.snapshot.activeTabId) return;
      runGuarded(tabs.snapshot.activeTabId, () => {
        tabs.setTabDirty(tabs.snapshot.activeTabId, false);
        tabs.selectTab(tabId);
        navigateToTab(tab);
      });
    },
    [navigateToTab, runGuarded, tabs],
  );

  const createContextTab = useCallback(() => {
    const activeTabId = tabs.snapshot.activeTabId;
    runGuarded(activeTabId, () => {
      tabs.setTabDirty(activeTabId, false);
      const result = tabs.createTab(defaultNewTabLocation);
      if (result.status === 'created') {
        navigateToTab(defaultNewTabLocation);
      }
    });
  }, [defaultNewTabLocation, navigateToTab, runGuarded, tabs]);

  const closeContextTab = useCallback(
    (tabId: string, discardDirty = false) => {
      const current = tabs.snapshot;
      const index = current.tabs.findIndex((tab) => tab.id === tabId);
      const next = current.tabs[index + 1] ?? current.tabs[index - 1];
      const executeClose = () => {
        const result = tabs.closeTab(tabId, { discardDirty: true });
        if (
          result.status === 'closed' &&
          current.activeTabId === tabId &&
          next
        ) {
          navigateToTab(next);
        }
      };
      if (discardDirty) executeClose();
      else runGuarded(tabId, executeClose);
    },
    [navigateToTab, runGuarded, tabs],
  );

  const navigateInActiveTab = useCallback(
    (destination: string) => {
      const location = resolveContextTabLocation(destination, workspaceSlug);
      if (!location || location.url === tabs.activeTab.url) return;
      runGuarded(tabs.snapshot.activeTabId, () => {
        tabs.setTabDirty(tabs.snapshot.activeTabId, false);
        tabs.navigate(location);
        navigateToTab(location);
      });
    },
    [navigateToTab, runGuarded, tabs, workspaceSlug],
  );

  const navigateBack = useCallback(
    (fallback?: string) => {
      runGuarded(tabs.snapshot.activeTabId, () => {
        tabs.setTabDirty(tabs.snapshot.activeTabId, false);
        const result = tabs.goBack();
        if (result.status === 'navigated') {
          navigateToTab(result.location);
          return;
        }
        if (tabs.snapshot.tabs.length > 1) {
          closeContextTab(tabs.snapshot.activeTabId, true);
          return;
        }
        const location = fallback
          ? resolveContextTabLocation(fallback, workspaceSlug)
          : null;
        if (location) {
          tabs.navigate(location, { mode: 'replace' });
          pendingUrlRef.current = location.url;
          router.replace(location.url);
        } else {
          router.back();
        }
      });
    },
    [closeContextTab, navigateToTab, router, runGuarded, tabs, workspaceSlug],
  );

  const value = useMemo(
    () => ({
      ...tabs,
      currentRouteTabId,
      currentRoutePageKey,
      selectContextTab,
      createContextTab,
      closeContextTab,
      clearContextTabDirty,
      navigateInActiveTab,
      navigateBack,
    }),
    [
      closeContextTab,
      clearContextTabDirty,
      currentRoutePageKey,
      currentRouteTabId,
      createContextTab,
      navigateInActiveTab,
      navigateBack,
      selectContextTab,
      tabs,
    ],
  );

  return (
    <ContextTabsContext.Provider value={value}>
      {children}
      <ConfirmActionModal
        open={confirmOpen}
        title="Discard unsaved changes?"
        description="This tab has unsaved changes. Continuing will discard them."
        confirmButtonLabel="Discard and continue"
        cancelButtonLabel="Stay here"
        requireConfirmationText={false}
        onClose={() => {
          pendingActionRef.current = null;
          if (browserBackPendingRef.current) {
            browserBackPendingRef.current = false;
            armDirtyHistoryGuard(tabs.activeTab.id, tabs.activeTab.url);
          }
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          const action = pendingActionRef.current;
          pendingActionRef.current = null;
          setConfirmOpen(false);
          if (browserBackPendingRef.current) {
            action?.();
          } else {
            releaseDirtyHistoryGuard(action ?? undefined);
          }
        }}
      />
    </ContextTabsContext.Provider>
  );
}

export function useContextTabsContext() {
  return useContext(ContextTabsContext);
}
